import { DCR_NATIVE_STORE, DCR_ROUTES_BASE } from '../constants';
const unwrap = (payload) => payload && typeof payload === 'object' && 'data' in payload
    ? payload.data
    : payload;
/**
 * Every case and comment call declares which store it is for. Study eligibility for this
 * experience is decided here from MOM study metadata, which the org cannot see, and anything
 * that omits it keeps hitting Salesforce so the classic app is unaffected.
 */
const nativeStore = { store: DCR_NATIVE_STORE };
/**
 * A status change carries no signature, so the connector authorizes it from the caller's live
 * IAM role groups, read with this token, rather than the cortex roles provisioned into the org,
 * which only change when access is re-shared.
 */
const getIamSessionToken = () => {
    const match = document.cookie.match(/(?:^|;\s*)md_idtoken=([^;]*)/);
    return match?.[1] ? decodeURIComponent(match[1]) : undefined;
};
export const fetchDcrList = async (api, params = {}) => {
    // The connector reads filters as bracketed query keys (filter[dcr_intake__status]=Open)
    const filterParams = Object.entries(params.filter ?? {}).reduce((result, [key, value]) => {
        if (value !== '' && value !== undefined) {
            result[`filter[${key}]`] = value;
        }
        return result;
    }, {});
    const { data } = await api.get(DCR_ROUTES_BASE, {
        params: {
            ...nativeStore,
            ...(params.limit !== undefined && { limit: params.limit }),
            ...(params.offset !== undefined && { offset: params.offset }),
            ...(params.orderBy && { order_by: params.orderBy }),
            ...(params.order && { order: params.order }),
            ...(params.search && { search: params.search }),
            ...filterParams,
        },
    });
    return unwrap(data);
};
export const fetchDcr = async (api, dcrId) => {
    const { data } = await api.get(`${DCR_ROUTES_BASE}/${dcrId}`, { params: nativeStore });
    return unwrap(data);
};
export const fetchDcrComments = async (api, dcrId) => {
    const { data } = await api.get(`${DCR_ROUTES_BASE}/${dcrId}/comments`, {
        params: nativeStore,
    });
    return unwrap(data)?.records ?? [];
};
export const createDcrComment = async (api, dcrId, body) => {
    const { data } = await api.post(`${DCR_ROUTES_BASE}/${dcrId}/comments`, { dcr_intake__comment_body: body }, { params: nativeStore });
    return unwrap(data);
};
export const createDcr = async (api, payload, signature) => {
    const { data } = await api.post(DCR_ROUTES_BASE, { ...payload, ...signature }, { params: nativeStore });
    return unwrap(data);
};
export const updateDcr = async (api, dcrId, payload, signature) => {
    const { data } = await api.post(`${DCR_ROUTES_BASE}/${dcrId}`, { ...payload, ...signature }, { params: nativeStore });
    return unwrap(data);
};
export const cancelDcr = async (api, dcrId, note, signature) => {
    const { data } = await api.post(`${DCR_ROUTES_BASE}/${dcrId}/cancellation`, { dcr_intake__note: note, ...signature }, { params: nativeStore });
    return unwrap(data);
};
export const changeDcrStatus = async (api, dcrId, status, extra = {}) => {
    const { data } = await api.post(`${DCR_ROUTES_BASE}/${dcrId}/status`, {
        dcr_intake__status: status,
        ...(extra.action !== undefined && { dcr_intake__action: extra.action }),
        ...(extra.note !== undefined && { dcr_intake__note: extra.note }),
        iamToken: getIamSessionToken(),
    }, { params: nativeStore });
    return unwrap(data);
};
/**
 * Carries out the change the request asks for. The change itself is not restated: the connector
 * runs what the site user signed for, and resolves the request once it has.
 */
export const executeDcr = async (api, dcrId, signature) => {
    const { data } = await api.post(`${DCR_ROUTES_BASE}/${dcrId}/execution`, { ...signature }, { params: nativeStore });
    return unwrap(data);
};
export const fetchDcrSites = async (api) => {
    const { data } = await api.get(`${DCR_ROUTES_BASE}/sites`);
    return unwrap(data) ?? [];
};
/**
 * The connector publishes its version as the public org config dcr_intake__version. Cortex has
 * no read route for a single config, so it comes through the org's public configuration
 * include. Undefined means the config is absent, which only happens on connectors that predate
 * this experience.
 */
export const fetchDcrConnectorVersion = async (api) => {
    const { data } = await api.get('/v2/orgs', {
        params: { 'include[]': 'configuration.public', 'limit': 1 },
    });
    return unwrap(data)?.[0]?.configuration?.public?.dcr_intake__version?.version;
};
export const fetchDcrParticipantByNumber = async (api, siteId, participantNumber) => {
    const { data } = await api.get(`${DCR_ROUTES_BASE}/sites/${siteId}/public_users`, {
        params: { where: JSON.stringify({ c_number: participantNumber }) },
    });
    return (unwrap(data) ?? [])[0];
};
export const fetchDcrParticipantVisits = async (api, participantId) => {
    const { data } = await api.get(`${DCR_ROUTES_BASE}/public_users/${participantId}/visits`);
    return unwrap(data) ?? [];
};
/**
 * The participant's own schedule, which unlike the connector's visit list carries the scheduled
 * date we show as the original value when a visit date is being corrected.
 */
export const fetchDcrParticipantVisitSchedule = async (api, siteId, participantId) => {
    const { data } = await api.get(`/v2/routes/c_sites/${siteId}/c_public_users/${participantId}/c_visit_schedule`);
    return unwrap(data) ?? [];
};
/** Every response recorded for the participant, with the activity expanded */
export const fetchDcrParticipantTaskResponses = async (api, participantId) => {
    const { data } = await api.get(`${DCR_ROUTES_BASE}/public_users/task_responses/${participantId}`);
    return unwrap(data) ?? [];
};
export const fetchDcrBranchingFlow = async (api, taskResponseId) => {
    const { data } = await api.get(`${DCR_ROUTES_BASE}/branching_logic/flow`, {
        params: { task_response_id: taskResponseId },
    });
    return unwrap(data);
};
